import { ref, computed, watch } from 'vue';

import { defineStore } from 'pinia';

// Dev-only runtime override key. Lets a developer on a real dev build simulate the
// production environment (hide envOnly:'development' nav, trip the dev-only route
// guards) without rebuilding — and revert. Persisted per browser.
const OVERRIDE_KEY = 'wp.dev.envOverride';

// Dev Settings → "Show dev-only pages", kept in sessionStorage (per tab session) on every
// build. Unset, it follows the effective environment: ON in development, OFF elsewhere.
// In development it can hide the envOnly:'development' nav items and trip the dev-only route
// guards, as on production. Elsewhere it lifts them for super admins only (the consumers
// require user.superAdmin, so the flag alone does nothing for regular users). The old
// localStorage copy of this key is no longer read and is left in place.
const SHOW_DEV_ONLY_KEY = 'wp.dev.showDevOnlyPages';

export const useEnvironmentStore = defineStore('environment', () => {
  const apiBaseUrl = ref(import.meta.env.VITE_API_BASE_URL);

  // The real environment, fixed at build time by the mode's .env file (VITE_APP_ENV), never
  // by the URL the app is served from. Anything but 'development', including a missing value,
  // counts as production, so a misconfigured build hides dev-only features rather than
  // exposing them. Source of truth, never mutated.
  const detectedEnvironment = ref(
    import.meta.env.VITE_APP_ENV === 'development' ? 'development' : 'production',
  );

  // The simulation control is offered ONLY when the real environment is development,
  // so a Management user on real production can never reveal unfinished dev features.
  const canSimulateEnvironment = computed(() => detectedEnvironment.value === 'development');

  const loadOverride = () => {
    try {
      const raw = localStorage.getItem(OVERRIDE_KEY);
      return raw === 'development' || raw === 'production' ? raw : null;
    } catch {
      return null;
    }
  };

  // null → use detected. Only honored on a real dev build (guarded above).
  const environmentOverride = ref(canSimulateEnvironment.value ? loadOverride() : null);

  watch(environmentOverride, (value) => {
    try {
      if (value) localStorage.setItem(OVERRIDE_KEY, value);
      else localStorage.removeItem(OVERRIDE_KEY);
    } catch {
      // ignore — dev tool, persistence is best-effort
    }
  });

  // The explicit choice made in this tab session: true, false, or null when never set.
  const loadShowDevOnlyPages = () => {
    try {
      const raw = sessionStorage.getItem(SHOW_DEV_ONLY_KEY);
      return raw === 'true' ? true : raw === 'false' ? false : null;
    } catch {
      return null;
    }
  };

  const showDevOnlyPagesChoice = ref(loadShowDevOnlyPages());

  const setShowDevOnlyPages = (value) => {
    showDevOnlyPagesChoice.value = !!value;
    try {
      sessionStorage.setItem(SHOW_DEV_ONLY_KEY, String(!!value));
    } catch {
      // ignore — dev tool, persistence is best-effort
    }
  };

  // What the whole app behaves as: override (when allowed) → detected.
  const effectiveEnvironment = computed(
    () => environmentOverride.value ?? detectedEnvironment.value,
  );

  // Kept for backward-compat with the few readers of `environment`; now reflects the
  // effective (possibly simulated) environment.
  const environment = effectiveEnvironment;

  const isDevelopment = computed(() => effectiveEnvironment.value === 'development');
  const isProduction = computed(() => effectiveEnvironment.value === 'production');

  // The switch's value: the tab's explicit choice, else ON only in (effective) development.
  const showDevOnlyPages = computed(() => showDevOnlyPagesChoice.value ?? isDevelopment.value);

  // True while a simulation differs from the real environment — drives the persistent
  // header "Simulating …" chip so the override is always discoverable and reversible.
  const isEnvOverrideActive = computed(
    () => environmentOverride.value !== null
      && environmentOverride.value !== detectedEnvironment.value,
  );

  const setEnvironmentOverride = (value) => {
    if (!canSimulateEnvironment.value) return; // never simulate from real production
    environmentOverride.value =
      value === 'development' || value === 'production' ? value : null;
  };

  const resetEnvironment = () => {
    environmentOverride.value = null;
  };

  return {
    apiBaseUrl,
    environment,
    detectedEnvironment,
    effectiveEnvironment,
    environmentOverride,
    canSimulateEnvironment,
    isEnvOverrideActive,
    isDevelopment,
    isProduction,
    setEnvironmentOverride,
    resetEnvironment,
    showDevOnlyPages,
    setShowDevOnlyPages,
  };
});
